import { Link } from "@inertiajs/react";
import Screenshot from "@/components/help/Screenshot";
import { RunningCampaignHero, LockedCampaignHero } from "@/components/schools/DashboardHeros";
import SetupSummaryCard from "@/components/schools/setup/SetupSummaryCard";
import {
  MEMBERSHIP_ADMIN,
  CAMPAIGN_ACTIVE,
  CAMPAIGN_LOCKED,
  SCHOOL_MILESTONES,
  SETUP_FRESH,
  SETUP_IN_PROGRESS,
} from "@/help/fixtures";

const runningFixture = {
  ...CAMPAIGN_ACTIVE,
  description: "",
  classroom_count: 9,
  student_count: 184,
  edit_path: "#",
  import_classrooms_path: "#",
  milestones_path: "#",
  milestones: SCHOOL_MILESTONES,
};

const lockedFixture = {
  ...CAMPAIGN_LOCKED,
  classroom_count: 9,
  student_count: 184,
  edit_path: "#",
  milestones: SCHOOL_MILESTONES,
};

export default function SchoolAdminDashboard() {
  return (
    <>
      <p>
        Your dashboard is where you'll spend most of your time. It looks a little different depending on where you are in the campaign — the most useful info is always right at the top.
      </p>

      <h2>Your first day</h2>
      <p>
        Your first campaign is already waiting as a draft. The dashboard shows the setup guide's five stages, from your school's details to launch day, the campaign fee, and the step to start with.
      </p>
      <Screenshot caption="A brand-new dashboard">
        <SetupSummaryCard setup={SETUP_FRESH} />
      </Screenshot>

      <h2>Getting ready to launch</h2>
      <p>
        While setup is unfinished, the dashboard shows how far along it is, stage by stage, and the one thing to do next. Click through to the setup guide for the details of every step. Once donations open, the campaign shows as <strong>Donations open</strong> and a card below has your school's donation page link to share. Once every setup task is done or skipped and donations are open, you’ll see the main dashboard with a small banner showing when reading opens. The setup guide stays available in the sidebar. See <Link href="/help/articles/getting-started-school-admin">Getting your school set up</Link> for a tour of the steps.
      </p>
      <Screenshot caption="Setup in progress">
        <SetupSummaryCard setup={SETUP_IN_PROGRESS} />
      </Screenshot>

      <h2>While the campaign is running</h2>
      <p>
        Now the dashboard does its real job: showing you how things are going at a glance. You'll see how much you've raised, how many minutes have been read, how many days are left, and which rewards have unlocked. Below that, leaderboards for top students and top classrooms.
      </p>
      <Screenshot caption="Mid-campaign dashboard">
        <RunningCampaignHero campaign={runningFixture} membership={MEMBERSHIP_ADMIN} />
      </Screenshot>

      <h2>After it wraps up</h2>
      <p>
        Once the campaign closes, the dashboard freezes onto the final results. There's a button to start your next campaign whenever you're ready (each campaign has its own <Link href="/help/articles/paying-for-your-campaign">fee</Link>), and you can always come back to past campaigns from the <strong>Past campaigns</strong> button.
      </p>
      <Screenshot caption="Final results">
        <LockedCampaignHero campaign={lockedFixture} membership={MEMBERSHIP_ADMIN} new_campaign_path="#" />
      </Screenshot>

      <h2>The sidebar</h2>
      <p>
        On the left, you've got everything else: the setup guide (until you launch), school-wide rewards, student prizes, your classrooms, the list of donations, your team members, and settings for the school itself, including your Square connection.
      </p>

      <h2>What's next</h2>
      <ul>
        <li><Link href="/help/articles/tracking-donations">Tracking donations</Link>.</li>
        <li><Link href="/help/articles/campaign-lifecycle">The four stages of a campaign</Link>.</li>
        <li><Link href="/help/articles/after-the-campaign">After the campaign ends</Link>.</li>
      </ul>
    </>
  );
}
